"use client";

import type { CatalogItem } from "@/lib/types";
import { carvetConfig } from "@/lib/types";
import { getBrandLogoSrc, handleBrandLogoError } from "@/lib/brandLogo";

interface ProductCardProps {
  item: CatalogItem;
  onLead: (id: string) => void;
  onPhoto: (id: string) => void;
}

export function ProductCard({ item, onLead, onPhoto }: ProductCardProps) {
  const powerClean =
    item.power && !item.power.includes("DOHC") ? item.power : "150 л.с.";
  const isKpp = item.categorySlug === "kpp";
  const cfg = carvetConfig;
  const btnLabel = isKpp ? cfg.btnTextKpp : cfg.btnTextEngine;
  const codeTitle = isKpp ? "Маркировка / Код КПП:" : "Маркировка / Код ДВС:";
  const rightBannerTitle = isKpp ? "Тип узла:" : "Коробка передач:";
  const rightBannerValue = isKpp ? "КПП" : item.transmissionType || "Не указано";
  const productImage = isKpp ? "/img/kpp.jpg" : "/img/engine.webp";

  const stockBadge =
    cfg.showStockCount !== false && item.inStockCount ? (
      <span className="badge-stock">
        ● {item.stockStatus} ({item.inStockCount} шт.)
      </span>
    ) : (
      <span className="badge-stock">● {item.stockStatus}</span>
    );

  return (
    <div className="product-card">
      <div className="card-top">
        <span className="badge-cat">{item.category}</span>
        {stockBadge}
      </div>

      <div className="card-image-box">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={productImage}
          alt={`${item.brand} ${item.engineCode}`}
          className="card-engine-thumb"
          loading="lazy"
        />
        <button
          type="button"
          className="btn-request-photo-badge"
          onClick={() => onPhoto(item.id)}
        >
          <span>📸 Живые фото со склада</span>
        </button>
      </div>

      <div className="car-header-title">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={getBrandLogoSrc(item.brand)}
          alt={item.brand}
          className="card-brand-logo"
          onError={(e) => handleBrandLogoError(e, item.brand)}
        />
        <span>
          {item.brand} {item.model} ({item.year})
        </span>
      </div>

      <div className="engine-code-banner">
        <div>
          <div className="code-title">{codeTitle}</div>
          <div className="code-value">{item.engineCode}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
            {rightBannerTitle}
          </div>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "var(--text-main)",
            }}
          >
            {rightBannerValue}
          </div>
        </div>
      </div>

      <table className="specs-table">
        <tbody>
          {cfg.showManufacturer && item.manufacturer && (
            <tr>
              <td className="label">Производитель:</td>
              <td className="val">{item.manufacturer}</td>
            </tr>
          )}
          {cfg.showTargetEngine !== false && isKpp && item.targetEngineCode && (
            <tr>
              <td className="label">Совместим с ДВС:</td>
              <td
                className="val"
                style={{ fontWeight: 600, color: "var(--primary-blue)" }}
              >
                {item.targetEngineCode}
              </td>
            </tr>
          )}
          {cfg.showKppAliases !== false && isKpp && item.kppAliases && (
            <tr>
              <td className="label">Доп. маркировки:</td>
              <td className="val">{item.kppAliases}</td>
            </tr>
          )}
          {cfg.showSpecsVolumeFuel !== false && (
            <tr>
              <td className="label">Объем / Топливо:</td>
              <td className="val">
                {item.displacement} ({item.fuel})
              </td>
            </tr>
          )}
          {cfg.showSpecsPower !== false && powerClean && (
            <tr>
              <td className="label">Мощность:</td>
              <td className="val">{powerClean}</td>
            </tr>
          )}
          {cfg.showDriveType !== false && item.driveType && (
            <tr>
              <td className="label">Привод:</td>
              <td className="val">{item.driveType}</td>
            </tr>
          )}
          {cfg.showMileage !== false && item.mileage && (
            <tr>
              <td className="label">Пробег донора:</td>
              <td className="val">{item.mileage}</td>
            </tr>
          )}
          {cfg.showOrigin !== false && item.origin && (
            <tr>
              <td className="label">Страна ввоза:</td>
              <td className="val">{item.origin}</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="card-footer">
        <button
          type="button"
          className="btn btn-orange btn-card-order"
          onClick={() => onLead(item.id)}
        >
          <span>{btnLabel}</span>
        </button>
      </div>
    </div>
  );
}
